// Dependencies
import React, { Fragment, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";

// Styles
import { Container } from "./styles";

// Components
import { WebpOptionCategory } from "components/WebpOptionsList/WebpOptionCategory";
import { WebpOptionCheckbox } from "components/WebpOptionsList/WebpOptionCheckbox";
import { WebpOptionList } from "components/WebpOptionsList";
import { WebpSelect } from "components/WebpSelect";

// Types
enum AvailableControls {
    text = "TEXT",
    checkbox = "CHECKBOX",
    category = "CATEGORY",
    list = "LIST"
}

type WebpDropDownFilterProps = {
    id: string;
    data: any;
    filterPlaceholder: string;
    className: string;
    disabled?: boolean;
    onOptionSelected: (ev: any) => void;
    clearDropdownFilterSearchBox?: () => void;
};

type TextOptionProps = {
    option: any;
};

type CategoryOptionProps = {
    option: any;
    level: number;
};

type CheckboxOptionProps = {
    option: any;
    level: number;
    handleClickOption: (event: any, option: any) => void;
};

type ListOptionProps = {
    option: any;
    level: number;
    renderCallback: (options: any[], level: number) => void;
};

export const WebpDropdownFilter: FunctionComponent<WebpDropDownFilterProps> = ({
    id,
    data,
    filterPlaceholder,
    className,
    disabled,
    onOptionSelected,
    clearDropdownFilterSearchBox
}) => {
    const notFoundText = "Desculpe, não foi possível encontrar resultados para a sua busca.";

    const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
    const [validOptions, setValidOptions] = useState<string[]>([]);
    const [badgeContent, setBadgeContent] = useState<string>("");
    const [query, setQuery] = useState<string>("");

    const searchInString = (str: string, search: string) => {
        return str.toLowerCase().includes(search.toLowerCase());
    };

    const filteredData = useMemo(() => {
        return !!data?.options.length ? [...data.options] : [];
    }, [data]);

    const shouldRenderOptions = (options: any, query: any, validOptions: any[]) => {
        return (options && !query) || (!!query && validOptions.some((label) => searchInString(label, query)));
    };

    const handleClickOption = useCallback(
        (event: CustomEvent, option: any) => {
            const updatedOption = { ...option, checked: event.detail.checked };

            setSelectedOptions((prevSelectedOptions) => {
                const newOptions = updatedOption.checked
                    ? [...prevSelectedOptions, updatedOption]
                    : prevSelectedOptions.filter((item) => item.id !== updatedOption.id);

                onOptionSelected({ detail: { selectedOptions: newOptions, item: updatedOption } });

                if (clearDropdownFilterSearchBox) {
                    clearDropdownFilterSearchBox();
                }

                return newOptions;
            });
        },
        [clearDropdownFilterSearchBox, onOptionSelected]
    );

    const renderOptions = useCallback(
        (options: any[], level = 0) => {
            const renderByType = (option: any, level: number, query?: string) => {
                switch (option.type) {
                    case AvailableControls.text: {
                        return <TextOption key={`${option.label}-${level}`} option={option} />;
                    }

                    case AvailableControls.category: {
                        return <CategoryOption key={`${option.label}-${level}`} option={option} level={level} />;
                    }

                    case AvailableControls.checkbox: {
                        const hasValidOption = validOptions.findIndex((label) => label === option.label) > -1;

                        const checkboxLevel = level === 1 ? level : level - 1;

                        if (!hasValidOption) {
                            setValidOptions((prevValidOptions) => [...prevValidOptions, option.label]);
                        }

                        if (!query)
                            return (
                                <CheckboxOption
                                    key={`${option.label}-${level}`}
                                    option={option}
                                    level={checkboxLevel}
                                    handleClickOption={handleClickOption}
                                />
                            );

                        if (searchInString(option.label, query)) {
                            return (
                                <CheckboxOption
                                    key={`${option.label}-${level}`}
                                    option={option}
                                    level={checkboxLevel}
                                    handleClickOption={handleClickOption}
                                />
                            );
                        }

                        return null;
                    }

                    case AvailableControls.list: {
                        return <ListOption key={`${option.label}-${level}`} option={option} level={level} renderCallback={renderOptions} />;
                    }
                }
            };

            return options.map((option) => renderByType(option, level + 1, query));
        },
        [handleClickOption, query, validOptions]
    );

    const lookForCheckedOptions = useCallback((optionsList: any) => {
        optionsList.forEach((item: any) => {
            if (item.type === AvailableControls.list) {
                return lookForCheckedOptions(item.options);
            }

            setSelectedOptions((prevOptions) => {
                if (item.checked) {
                    if (!prevOptions.some((option) => option.id === item.id)) {
                        return [...prevOptions, item];
                    }
                } else {
                    return prevOptions.filter((option) => option.id !== item.id);
                }
                return prevOptions;
            });
        });
    }, []);

    const copyDataToState = useCallback(() => {
        setSelectedOptions([]);
        lookForCheckedOptions(filteredData);
    }, [filteredData, lookForCheckedOptions]);

    useEffect(() => {
        copyDataToState();
        setBadgeContent(selectedOptions.length > 0 ? selectedOptions.length.toString() : "");
    }, [copyDataToState, selectedOptions.length]);

    useEffect(() => {
        const handleSearchChange = (ev: any) => {
            if (ev.detail.id === id) {
                setQuery(ev.detail.value);
            }
        };

        document.addEventListener("webpDropdownSearchChanged", handleSearchChange);

        return () => {
            document.removeEventListener("webpDropdownSearchChanged", handleSearchChange);
        };
    }, [id]);

    return (
        <Container className={className}>
            <WebpSelect
                id={id}
                badgeContent={badgeContent}
                placeholder={filterPlaceholder}
                disabled={!!disabled}
                showSearch={true}
                handleClose={() => setQuery("")}
            >
                {shouldRenderOptions(filteredData, query, validOptions) ? (
                    renderOptions(filteredData)
                ) : (
                    <p className="not-found-text">{notFoundText}</p>
                )}
            </WebpSelect>
        </Container>
    );
};

export const TextOption: FunctionComponent<TextOptionProps> = ({ option }) => <span className="text">{option.label}</span>;

export const CategoryOption: FunctionComponent<CategoryOptionProps> = ({ option, level }) => (
    <WebpOptionCategory label={option.label} level={level.toString()} />
);

export const CheckboxOption: FunctionComponent<CheckboxOptionProps> = ({ option, level, handleClickOption }) => {
    return (
        <WebpOptionCheckbox
            label={option.label}
            value={option.label}
            level={level.toString()}
            checked={option.checked}
            labelOrder="label-after"
            layout="checkbox"
            onWebpCheckboxChange={(e: any) => {
                handleClickOption(e, option);
            }}
        />
    );
};

export const ListOption: FunctionComponent<ListOptionProps> = ({ option, level, renderCallback }) => {
    return (
        <WebpOptionList
            className={`webp-options-list level-${level}`}
            contentComposition={
                <Fragment>
                    {option.label && <CategoryOption option={option} level={level} />}

                    {renderCallback(option.options, level)}
                </Fragment>
            }
        />
    );
};
